import { promotionLineKey, type SavedCartPromotion } from '@/features/marketplace/cartPromotion';
import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/lib/auth/AuthProvider";
import { apiFetch, ApiError } from "@/lib/api/client";
import { getStripe } from "@/lib/stripe";
import { cart, type CartItem } from "@/features/marketplace/stores/cart";
import type {
  LabCartPurchaseInput,
  LabCheckoutResponse,
  LabCheckoutStatus,
} from "@/types/labs";
import { useLabEvent } from "./labExperiment";

const key = (userId: string) => `system.labs.cart-purchase.${userId}`;
export function readLabCartAttempt(
  userId?: string,
): LabCartPurchaseInput | null {
  if (!userId) return null;
  try {
    const body = JSON.parse(localStorage.getItem(key(userId)) || "null");
    return body?.request_id && body?.checkout_token && body?.product_id
      ? body
      : null;
  } catch {
    return null;
  }
}
export function useLabCartPurchase() {
  const { session } = useAuth();
  const userId = session?.user.id;
  const current = useRef(userId);
  current.current = userId;
  const queryClient = useQueryClient();
  const emit = useLabEvent();
  const [attempt, setAttempt] = useState<LabCartPurchaseInput | null>(() =>
    readLabCartAttempt(userId),
  );
  const [error, setError] = useState<string | null>(null);
  // `lab_profile_incomplete` sends the patient to the Account profile editor.
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    current.current = userId;
    setAttempt(readLabCartAttempt(userId));
    setError(null);
    setErrorCode(null);
    setBusy(false);
    return () => {
      current.current = undefined;
    };
  }, [userId]);
  function clear(owner: string) {
    localStorage.removeItem(key(owner));
    if (current.current === owner) setAttempt(null);
  }
  async function purchaseOne(
    items: CartItem[],
    paymentMethod: string,
    promotion?: SavedCartPromotion | null,
  ): Promise<boolean> {
    if (!userId) return false;
    const owner = userId;
    let saved = readLabCartAttempt(owner);
    if (!saved) {
      const item = items[0];
      if (!item || items.length !== 1) {
        setError("Choose one blood test at a time.");
        return false;
      }
      const promotionLine = promotion?.quote.items.find((line) => line.key === promotionLineKey(item));
      if (promotion && !promotionLine) { setError('The blood test promotion needs to be reviewed before purchasing.'); return false; }
      saved = {
        ...(promotion ? { cart_promotion_id: promotion.quote.id, cart_promotion_line_key: promotionLineKey(item) } : {}),
        product_id: item.id,
        request_id: crypto.randomUUID(),
        checkout_token:
          crypto.randomUUID().replace(/-/g, "") +
          crypto.randomUUID().replace(/-/g, ""),
        expected_amount_cents: promotionLine?.total_cents ?? item.price_cents,
        payment_method_id: paymentMethod,
        purchase_type: item.lab_purchase_type ?? "subscription",
        interval_months:
          item.lab_purchase_type === "one_time"
            ? null
            : (item.lab_interval_months ?? 1),
        consent: true,
      };
      // Persist before the first request. Losing this identity must stop checkout.
      try {
        localStorage.setItem(key(owner), JSON.stringify(saved));
      } catch {
        setError("Enable browser storage before checking out.");
        return false;
      }
    }
    setAttempt(saved);
    setError(null);
    setErrorCode(null);
    setBusy(true);
    emit("blood_test_checkout_started", {
      source: "cart",
      product_id: saved.product_id,
      purchase_type: saved.purchase_type,
      interval_months: saved.interval_months ?? undefined,
    });
    try {
      const response = await apiFetch<LabCheckoutResponse>(
        "/me/lab-purchases",
        { method: "POST", body: JSON.stringify(saved) },
      );
      if (current.current !== owner) return false;
      let result: LabCheckoutStatus | LabCheckoutResponse = response;
      if (response.status !== "paid") {
        if (
          response.payment_status === "requires_action" &&
          response.client_secret
        ) {
          const stripe = await getStripe(response.publishable_key);
          if (current.current !== owner) return false;
          if (!stripe)
            throw new Error(
              "Card verification could not load. Retry this payment.",
            );
          const verified = await stripe.confirmCardPayment(
            response.client_secret,
          );
          if (current.current !== owner) return false;
          if (verified.error)
            throw new Error(
              verified.error.message || "Card verification is still required.",
            );
        }
        result = await apiFetch<LabCheckoutStatus>("/labs/checkout/status", {
          method: "POST",
          body: JSON.stringify({
            checkout_id: saved.request_id,
            checkout_token: saved.checkout_token,
          }),
        });
      }
      if (current.current !== owner) return false;
      if (result.status === "paid" && result.lab_order_id) {
        clear(owner);
        cart.remove(saved.product_id);
        await queryClient.invalidateQueries({ queryKey: ["me", "lab-orders"] });
        await queryClient.invalidateQueries({ queryKey: ["me", "account-credit"] });
        await queryClient.invalidateQueries({
          queryKey: ["me", "lab-subscriptions"],
        });
        if (current.current !== owner) return false;
        emit("blood_test_confirmation_viewed", {
          source: "cart",
          product_id: saved.product_id,
          purchase_type: saved.purchase_type,
        });
        return true;
      }
      if (["cancelled", "refunded"].includes(result.status)) {
        clear(owner);
        throw new Error(
          "This payment is closed. Review your cart before trying again.",
        );
      }
      throw new Error(
        "Your payment needs to be checked. Resume this payment or cancel the unpaid purchase before changing your cart.",
      );
    } catch (cause) {
      if (current.current !== owner) return false;
      if (
        cause instanceof ApiError &&
        (cause.details as { checkout_not_created?: boolean } | null)
          ?.checkout_not_created
      )
        clear(owner);
      setError(
        cause instanceof Error
          ? cause.message
          : "Payment is still being checked. Please retry this saved payment.",
      );
      setErrorCode(cause instanceof ApiError ? cause.code : null);
      emit("blood_test_checkout_failed", {
        source: "cart",
        reason: cause instanceof ApiError ? cause.code : "payment_pending",
      });
      return false;
    } finally {
      if (current.current === owner) setBusy(false);
    }
  }
  async function purchase(
    items: CartItem[],
    paymentMethod: string,
    promotion?: SavedCartPromotion | null,
  ): Promise<boolean> {
    // Finish the durable in-flight receipt before considering another panel.
    // A failure leaves purchased items removed and the remaining cart intact.
    if (readLabCartAttempt(userId)) return purchaseOne([], paymentMethod);
    if (!items.length) return purchaseOne([], paymentMethod);
    const owner = userId;
    for (const item of items) {
      if (
        current.current !== owner ||
        !(await purchaseOne([item], paymentMethod, promotion))
      )
        return false;
    }
    return true;
  }
  async function cancel() {
    if (!userId || !attempt) return;
    const owner = userId;
    setBusy(true);
    setError(null);
    setErrorCode(null);
    try {
      if (!navigator.locks?.request)
        throw new Error(
          "Please use an up-to-date browser to cancel this purchase safely.",
        );
      const result = await navigator.locks.request(
        `system.portal.purchase.${owner}`,
        async () => {
          if (current.current !== owner)
            throw new Error("Your account changed.");
          return await apiFetch<{ status: string }>(
            `/me/lab-purchases/${attempt.request_id}/cancel`,
            {
              method: "POST",
              body: JSON.stringify({
                checkout_id: attempt.request_id,
                checkout_token: attempt.checkout_token,
              }),
            },
          );
        },
      );
      if (current.current !== owner) return;
      if (result.status === "paid") {
        setError("Payment completed. Resume to open your test.");
        return;
      }
      clear(owner);
    } catch (cause) {
      if (current.current === owner)
        setError(
          cause instanceof Error
            ? cause.message
            : "Payment is still being checked.",
        );
    } finally {
      if (current.current === owner) setBusy(false);
    }
  }
  return { attempt, error, errorCode, busy, purchase, cancel };
}
